/**
 * Front-channel single sign-out: `GET /sso-logout`.
 *
 * When somebody signs out at their Home, the Home walks the list of relying apps they are connected
 * to and navigates, top level, to each app's `/sso-logout` with a `return` pointing back at itself.
 * An app that does not answer stays signed in behind the Home's back. This page answers: it reads
 * the token, asks the API to drop the server-side session, forgets it locally, and moves on.
 *
 * Two rules: the `return` is an OPEN REDIRECT unless it is checked (only our own origin or the
 * person's own Home zone are followed), and it must be safe with no session at all, and twice.
 */

/** Where the browser keeps the session. Defined here so the logout page and the app cannot drift. */
export const SESSION_KEY = 'heartcoach.session';

export interface LogoutAllowlist {
  selfOrigin: string;
  homeOrigin?: string | null;
  homeZone?: string | null;
}

const LABEL_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

export function safeReturnTo(raw: string | null | undefined, allow: LogoutAllowlist): string {
  const home = `${allow.selfOrigin.replace(/\/+$/, '')}/`;
  if (!raw) return home;
  let u: URL;
  try {
    u = new URL(raw, allow.selfOrigin);
  } catch {
    return home;
  }
  if (u.origin === allow.selfOrigin) return u.toString();
  if (allow.homeOrigin && u.origin === allow.homeOrigin) return u.toString();
  return isHomeZoneOrigin(u, allow.homeZone) ? u.toString() : home;
}

function isHomeZoneOrigin(u: URL, zone: string | null | undefined): boolean {
  const z = (zone ?? '').trim().toLowerCase();
  if (!z) return false;
  const host = u.hostname.toLowerCase();
  const isLocal = host === 'localhost' || host === '127.0.0.1';
  if (isLocal) return z === 'localhost';
  if (u.protocol !== 'https:') return false;
  if (host === z) return true;
  if (!host.endsWith(`.${z}`)) return false;
  return LABEL_RE.test(host.slice(0, -(z.length + 1)));
}

export const SIGN_OUT_WAIT_MS = 5000;

export interface LogoutPageOptions {
  apiBase: string;
  returnTo: string;
}

/** The whole page, self-contained. Deliberately NOT the SPA: it boots no router and mounts no React. */
export function logoutPageHtml({ apiBase, returnTo }: LogoutPageOptions): string {
  const cfg = jsonForScript({ apiBase: apiBase.replace(/\/+$/, ''), returnTo, key: SESSION_KEY, waitMs: SIGN_OUT_WAIT_MS });
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="robots" content="noindex">
<title>Signing you out — HeartCoach</title>
<style>
  :root { color-scheme: light dark; }
  body { margin: 0; display: grid; place-items: center; min-height: 100vh;
         font: 16px/1.5 "Inter", system-ui, -apple-system, sans-serif; background: #fbf7f6; color: #1b1b1f; }
  @media (prefers-color-scheme: dark) { body { background: #121214; color: #ece9ea; } }
  main { text-align: center; padding: 2rem; }
  a { color: inherit; }
</style>
</head>
<body>
<main>
  <h1>Signing you out…</h1>
  <p>Ending your HeartCoach session.</p>
  <p><a id="onward" href="${escapeAttr(returnTo)}">Continue</a></p>
</main>
<script>
(function () {
  var C = ${cfg};
  var done = false;
  function onward() { if (done) return; done = true; location.replace(C.returnTo); }
  var token = null;
  try {
    var raw = localStorage.getItem(C.key);
    if (raw) token = (JSON.parse(raw) || {}).token || null;
  } catch (e) {}
  try { localStorage.removeItem(C.key); } catch (e) {}
  if (!token) return onward();
  setTimeout(onward, C.waitMs);
  fetch(C.apiBase + '/auth/signout', {
    method: 'POST',
    keepalive: true,
    headers: { 'content-type': 'application/json', authorization: 'Bearer ' + token },
    body: '{}'
  }).then(onward, onward);
})();
</script>
<noscript><meta http-equiv="refresh" content="0;url=${escapeAttr(returnTo)}"></noscript>
</body>
</html>
`;
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** JSON safe inside a `<script>`: the characters that can end a script element are escaped. */
function jsonForScript(value: unknown): string {
  return JSON.stringify(value).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026').replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029');
}
